import { type BasketFee, type PaymentDetails, type SavedCard, PAYMENT_TYPES } from '@koala/sdk';
import { CreditCardBrandNamesToFileNameMap } from '@/constants/payment';
import { DECLINE_ERROR_COPY, type DeclineCopy } from '@/constants/paymentErrorCopy';
import { REGEX, REGEX_VALIDATION_STRINGS } from '@/constants/validation';

/**
 * Whether a saved card's expiry date is in the past.
 * Uses `is_expired` when the API provides it, otherwise compares the "YYYY-MM" expiry string
 * against the current date. Cards missing both brand and expiry are treated as expired
 * because coo-api requires those fields on submit.
 */
export function isCardExpired(card: SavedCard): boolean {
  if (card.is_expired) return true;
  if (!card.card_type || !card.expires) return true;
  const [year, month] = card.expires.split('-');
  if (!year || !month) return true;
  const now = new Date();
  const expiryDate = new Date(Number(year), Number(month), 1);
  return expiryDate < now;
}

/*
 * Function that maps given brand name to icon credit card icon file name stored in
 * //public/static/img/card-icons/placeholder
 */
export const mapCardBrandToFileName = (brandName: string): string => {
  const normalized = brandName.toLowerCase();
  return (
    Object.keys(CreditCardBrandNamesToFileNameMap).find((key) =>
      // @ts-expect-error `string` can't index `CreditCardBrandNamesToFileNameMap`.
      CreditCardBrandNamesToFileNameMap[key].some((v: string) => v.toLowerCase() === normalized),
    ) ?? 'card-empty'
  );
};

export const getZipCodeValidationMessage = (zipCode: string | null) => {
  if (!zipCode) {
    return 'Zip code is empty';
  }

  if (zipCode && !REGEX.ZIP_CODE_USA_AND_POSTAL_CODE_CANADA.test(zipCode)) {
    return REGEX_VALIDATION_STRINGS.ZIP_CODE_USA_AND_POSTAL_CODE_CANADA;
  }

  return null;
};

/*
 * Function that is used in order confirmation page so far. It returns payment information string based on payment type
 */
export const getPaymentDisplayInfo = (payment: PaymentDetails, index: number) => {
  let paymentString = 'N/A';

  // Derive the display string based on the payment type
  switch (payment.type) {
    case PAYMENT_TYPES.CREDIT_CARD:
    case PAYMENT_TYPES.CREDIT_CARD_TOKEN:
    case PAYMENT_TYPES.DIGITAL_WALLET:
      paymentString =
        !!payment.card_brand && !!payment.last_four
          ? `${payment.card_brand} ${payment.last_four}`
          : paymentString;
      break;
    case PAYMENT_TYPES.CASH:
      paymentString = 'Cash';
      break;
    case PAYMENT_TYPES.STORED_VALUE:
      paymentString = 'Gift card';
      break;
    case PAYMENT_TYPES.BILLING_ACCOUNT:
      paymentString = !!payment.saved_account_id
        ? `Account #${payment.saved_account_id}`
        : paymentString;
      break;
  }

  // Add a comma if we have multiple payment methods
  if (index > 0) {
    paymentString = `, ${paymentString}`;
  }

  return paymentString;
};

export const getCurrencyCodeByCountryAbbreviation = (countryCode: string) => {
  switch (countryCode.toLowerCase()) {
    case 'us':
      return 'usd';
    case 'ca':
      return 'cad';
    default:
      return 'usd';
  }
};

// coo-api's Basket model defaults `fees` to null and the SDK passes a failed parse through,
// so a basket without a fee list must total 0 rather than throw.
export const getTotalFeesAmount = (fees: BasketFee[] | null | undefined) =>
  (fees ?? []).map((fee) => fee.amount).reduce((prev, current) => prev + current, 0);

export const paymentReducerDigitalWalletMap: Record<number, string> = {
  1: 'Google Pay',
  2: 'Apple Pay',
};

export const paymentReducerDigitalWallet = (walletId?: number) => {
  return walletId ? paymentReducerDigitalWalletMap[walletId] : 'none';
};

// Detect payment-related errors (case-insensitive)
// e.g. Payment Error: Your card's security code is incorrect.
// e.g. Payment Error: Your card has insufficient funds.
// e.g. Payment Error: You cannot use a Stripe token more than once.
// e.g. Payment Error: Card nonce already used with a different idempotency key (Square).
// e.g. Credit Card x-7858 Declined: Invalid Billing Address and/or Zip Code.
// e.g. Credit Card x-8431 Failed: Unsuccessful Payment Transaction.
// e.g. A credit card is expired: 7 / 2020
export const isPaymentError = (message?: string) => {
  if (!message) return false;
  const lowerCaseMessage = message.toLowerCase();
  return (
    lowerCaseMessage.includes('payment error') ||
    lowerCaseMessage.includes('cannot use a stripe token more than once') ||
    lowerCaseMessage.includes('card nonce already used') ||
    lowerCaseMessage.includes('declined') ||
    lowerCaseMessage.includes('expired') ||
    lowerCaseMessage.includes('expiry') ||
    lowerCaseMessage.includes('unsuccessful payment transaction') ||
    lowerCaseMessage.includes('insufficient funds') ||
    lowerCaseMessage.includes('do not honor') ||
    lowerCaseMessage.includes('do_not_honor') ||
    lowerCaseMessage.includes('invalid cvc') ||
    lowerCaseMessage.includes('incorrect cvc') ||
    lowerCaseMessage.includes('invalid avs') ||
    lowerCaseMessage.includes('not able to be authorized') ||
    lowerCaseMessage.includes('error occurred processing your card')
  );
};

/**
 * Square reason codes, **in the priority order the ticket specifies**. A combined string names more
 * than one reason at once; the diner gets a single message for the first of these present, so the
 * most actionable cause wins rather than whichever one the provider happened to list first.
 *
 * The copy itself lives in `DECLINE_ERROR_COPY` (`@/constants/paymentErrorCopy`) with every other
 * diner-facing payment string, where one test enforces the shared constraints on all of it. The
 * match keys stay here, beside `isPaymentError` — see `resolveDeclineCopy` below. The `DeclineCopy`
 * value type is what stops a new reason being mapped to a literal written here.
 */
const SQUARE_DECLINE_COPY: readonly (readonly [string, DeclineCopy])[] = [
  ['CARD_EXPIRED', DECLINE_ERROR_COPY.cardExpired],
  ['CVV_FAILURE', DECLINE_ERROR_COPY.cvvMismatch],
  ['ADDRESS_VERIFICATION_FAILURE', DECLINE_ERROR_COPY.billingZipMismatch],
  ['TRANSACTION_LIMIT', DECLINE_ERROR_COPY.transactionLimit],
  ['INSUFFICIENT_FUNDS', DECLINE_ERROR_COPY.insufficientFunds],
  ['CARDHOLDER_INSUFFICIENT_PERMISSIONS', DECLINE_ERROR_COPY.cardholderRestricted],
  ['GENERIC_DECLINE', DECLINE_ERROR_COPY.genericDecline],
];

/** POS / Koala decline strings, matched case-insensitively anywhere in the text. */
const POS_DECLINE_COPY: readonly (readonly [string, DeclineCopy])[] = [
  ['invalid billing address and/or zip code', DECLINE_ERROR_COPY.billingAddressMismatch],
  ['credit card was not approved', DECLINE_ERROR_COPY.genericDecline],
  ['invalid cvv code', DECLINE_ERROR_COPY.cvvMismatch],
  ['unsuccessful payment transaction', DECLINE_ERROR_COPY.paymentNotProcessed],
];

/** Square: `Authorization error: 'CVV_FAILURE'`, or several reasons comma-combined in one string. */
const SQUARE_AUTHORIZATION_ERROR = /authorization error:\s*'([^']*)'/i;

/** POS: `Credit Card x-7858 Declined: <reason>` / `Credit Card x-8431 Failed: <reason>`. */
const POS_CARD_DECLINE = /credit card\s+x-\d+\s+(?:declined|failed)\s*:/i;

/**
 * TEAM-12668: turn a provider's decline text into copy that names the reason and the next step.
 *
 * The **match keys** live next to `isPaymentError` on purpose. That predicate already matches
 * several of these same decline strings in order to *route* the error and reset the single-use
 * payment token — it classifies but never rewrites. Co-locating them keeps one list of decline
 * strings instead of two that drift apart.
 *
 * The **copy** lives in `DECLINE_ERROR_COPY` (`@/constants/paymentErrorCopy`), for the mirror-image
 * reason: one governed list of diner-facing payment copy rather than a literal per call site, so
 * the shared constraints (no markup, length, no collision with the substring tables) are enforced
 * on all of it in one test (TEAM-13884). The split is the point — keys are matched against provider
 * text, copy is read by a diner, and they drift for different reasons.
 *
 * The return type stays `string | null` rather than narrowing to `DeclineCopy`: the governance
 * comes from the tables' value slot above, and narrowing here would be a breaking change for
 * callers comparing the result against an arbitrary string, for no added guarantee.
 *
 * Reads the raw upstream text (`prepareErrorMessage`'s `detail`), never `message`. See
 * docs/DINER_ERROR_MESSAGES.md: `detail` is the classification field, and these substrings only ever
 * appear in the backend's `error_description`.
 *
 * Returns `null` for anything that is not recognisably a decline, so the caller falls through to the
 * TEAM-13882 render rule rather than this function inventing copy for errors it does not understand.
 * The card reference (`x-NNNN`) needs no stripping step: every string returned here is authored, so
 * the reference cannot survive into diner-facing text.
 */
export const resolveDeclineCopy = (detail?: string): string | null => {
  if (!detail) return null;

  const squareMatch = SQUARE_AUTHORIZATION_ERROR.exec(detail);
  if (squareMatch) {
    const reasons = squareMatch[1].toUpperCase();
    /**
     * Only a reason this list knows. `Authorization error` is Square's wrapper for the whole
     * authorization step, not a verdict on the card — the same shape carries merchant, credential
     * and configuration failures (`UNAUTHORIZED`, `INVALID_LOCATION`). Treating every one of them as
     * a decline would tell a diner "your card was declined" for a store misconfiguration, and —
     * because the caller gates the single-use token reset on this same result — would throw away a
     * good payment token and log the failure as `paymentDenied`. Unknown reasons fall through to the
     * TEAM-13882 resolved copy, which is already generic, plain English and never the raw text.
     */
    return SQUARE_DECLINE_COPY.find(([reason]) => reasons.includes(reason))?.[1] ?? null;
  }

  const lowerCaseDetail = detail.toLowerCase();
  const mapped = POS_DECLINE_COPY.find(([match]) => lowerCaseDetail.includes(match));
  if (mapped) return mapped[1];

  // Unlike Square's wrapper, this shape *is* a verdict: the provider has said "Declined" or
  // "Failed" in the string itself, so an unmapped reason after it is still a decline.
  return POS_CARD_DECLINE.test(detail) ? DECLINE_ERROR_COPY.genericDecline : null;
};

/**
 * Minimal shape of Safari's `ApplePaySession` global. Only the static capability check is needed.
 * @see https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778027-canmakepayments
 */
interface ApplePaySessionStatics {
  canMakePayments?: () => boolean;
}

/**
 * Whether Apple Pay JS is usable in the current browser, i.e. whether Apple's native
 * `-webkit-appearance: -apple-pay-button` will actually paint and an `ApplePaySession` can start.
 *
 * Toast's hosted payment-method iframe lists Apple Pay regardless of browser capability, so the
 * submit button has to check for itself before rendering Apple's button (TEAM-14931).
 * `canMakePayments()` throws in insecure contexts, hence the try/catch.
 */
export const isApplePayAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;

  const session = (window as unknown as { ApplePaySession?: ApplePaySessionStatics })
    .ApplePaySession;

  try {
    return Boolean(session?.canMakePayments?.());
  } catch {
    return false;
  }
};
